import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";

export type CentreListItem = {
  id: number | string;
  name: string;
  countryId?: number;
  countryCode?: string;
  currencyCode?: string;
  active?: boolean;
  hasActiveSchools?: boolean;
  isActive?: boolean;
  IsActive?: boolean;
};

export function isCentreListItemActive(centre: CentreListItem): boolean {
  const raw = centre.active ?? centre.isActive ?? centre.IsActive;
  return raw === undefined ? true : !!raw;
}

export function positiveNumericCentreId(centre: CentreListItem): number | null {
  const raw = centre.id;
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

export function centreSelectOptionValue(centre: CentreListItem): number | string | null {
  const n = positiveNumericCentreId(centre);
  if (n != null) return n;
  const raw = centre.id;
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s === "" ? null : s;
}

export function centreOptionValuesEqual(a: number | string, b: unknown): boolean {
  if (b == null || b === "") return false;
  if (typeof a === typeof b) return a === b;
  return String(a) === String(b);
}

export function parsePositiveCentreId(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
}

export function extractCentreArrayPayload(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const key of ["items", "data", "centres", "value", "results", "records"]) {
      const v = o[key];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

function isPositiveIntLike(v: unknown): v is number | string {
  if (v == null || v === "") return false;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isFinite(n) && n > 0;
}

function pickBestCentreRowId(dto: Record<string, unknown>): number | string | null {
  const numericFirst = [
    dto.centreId,
    dto.CentreId,
    dto.centerId,
    dto.CenterId,
    dto.numericCentreId,
    dto.NumericCentreId,
    dto.id,
    dto.Id,
    dto.identifier,
  ];
  for (const c of numericFirst) {
    if (!isPositiveIntLike(c)) continue;
    const n = typeof c === "number" ? c : Number(String(c).trim());
    if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  }
  for (const c of numericFirst) {
    if (c == null || c === "") continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  return null;
}

function mapDtoToCentreListItem(dto: Record<string, unknown>): CentreListItem | null {
  if (!dto || typeof dto !== "object") return null;
  const idRaw = pickBestCentreRowId(dto);
  if (idRaw === null) return null;
  const name = String(dto.name ?? dto.Name ?? dto.centreName ?? dto.CentreName ?? "").trim();
  const countryIdRaw = dto.countryId ?? dto.CountryId;
  const countryId =
    countryIdRaw !== undefined && countryIdRaw !== null && countryIdRaw !== ""
      ? Number(countryIdRaw)
      : undefined;
  return {
    id: typeof idRaw === "number" ? idRaw : String(idRaw).trim(),
    name: name || String(idRaw),
    countryId: Number.isFinite(countryId) ? countryId : undefined,
    countryCode:
      dto.countryCode != null
        ? String(dto.countryCode)
        : dto.CountryCode != null
          ? String(dto.CountryCode)
          : undefined,
    currencyCode:
      dto.currencyCode != null
        ? String(dto.currencyCode)
        : dto.CurrencyCode != null
          ? String(dto.CurrencyCode)
          : undefined,
    active: dto.active as boolean | undefined,
    hasActiveSchools: dto.hasActiveSchools as boolean | undefined,
    isActive: dto.isActive as boolean | undefined,
    IsActive: dto.IsActive as boolean | undefined,
  };
}

export async function getCentres(): Promise<CentreListItem[]> {
  const api = await getLocalApi();
  const res = await api.call(apiConfig.centres_get_all, "GET", null);
  if (!res?.ok) throw new Error("Failed to fetch centres");
  const raw = await res.json();
  const rows = extractCentreArrayPayload(raw);
  const out: CentreListItem[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const mapped = mapDtoToCentreListItem(row as Record<string, unknown>);
    if (mapped) out.push(mapped);
  }
  return out;
}

export async function getCentreById(id: number): Promise<CentreListItem | null> {
  if (!(id > 0)) return null;
  const api = await getLocalApi();
  const template = (apiConfig as Record<string, string>).get_centre;
  if (!template) return null;
  const url = template.replace("{id}", String(Math.trunc(id)));
  const res = await api.call(url, "GET", null);
  if (!res?.ok) return null;
  const raw = await res.json();
  if (!raw || typeof raw !== "object") return null;
  return mapDtoToCentreListItem(raw as Record<string, unknown>);
}

export async function getCentresInDetails(): Promise<any[]> {
  const api = await getLocalApi();
  const res = await api.call(apiConfig.centres_get_all_in_details, "GET", null);
  if (!res?.ok) throw new Error("Failed to fetch centres (detailed)");
  return await res.json();
}

export function mergeCentreNamesIntoLookup(map: Map<number, string>, centres: CentreListItem[]): void {
  for (const c of centres) {
    const pid = positiveNumericCentreId(c);
    if (pid != null && c.name?.trim()) map.set(pid, c.name.trim());
  }
}

export const CENTRE_RAW_ID_KEYS = [
  "id",
  "Id",
  "centreId",
  "CentreId",
  "centreID",
  "CentreID",
  "centerId",
  "CenterId",
  "numericCentreId",
  "NumericCentreId",
  "schoolCentreId",
  "SchoolCentreId",
];

export function mergeRawCentreRowsIntoLookup(map: Map<number, string>, rows: unknown[]): void {
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const name = String(r.name ?? r.Name ?? r.centreName ?? r.CentreName ?? "").trim();
    if (!name) continue;
    for (const key of CENTRE_RAW_ID_KEYS) {
      const n = parsePositiveCentreId(r[key]);
      if (n != null) map.set(n, name);
    }
  }
}